import React, { useEffect, useState } from "react";
import AppLayout from "components/ui/AppLayout";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { reportsApi } from "api/reports";

function StatCard({ icon, label, value, sub, tone = "default" }) {
  const toneColor = { default: "var(--color-primary)", green: "#15803D", amber: "#B45309", red: "#B91C1C" }[tone];
  return (
    <div className="rounded-xl border p-5" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon name={icon} size={16} color={toneColor} />
        <span className="font-caption text-xs uppercase tracking-wide" style={{ color: "var(--color-text-secondary)" }}>
          {label}
        </span>
      </div>
      <p className="font-heading font-bold text-2xl" style={{ color: "var(--color-foreground)", letterSpacing: "-0.02em" }}>
        {value}
      </p>
      {sub && <p className="font-caption text-xs mt-1" style={{ color: "var(--color-text-secondary)" }}>{sub}</p>}
    </div>
  );
}

/**
 * Operations report (Sprint 14, Part 94 §94.2) — the support SLA surface:
 * first-reply times, off-hours auto-replies, escalations and AI token spend.
 */
export default function Operations() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = async () => {
    setIsLoading(true);
    setError("");
    try {
      const result = await reportsApi.getOps({ from: from || undefined, to: to || undefined });
      setData(result);
    } catch (err) {
      setError(err?.message || "Unable to load the operations report.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [from, to]);

  const avg = data?.reply?.avgFirstReplyMinutes;

  return (
    <AppLayout>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: "var(--color-foreground)", letterSpacing: "-0.02em" }}>
              Operations
            </h1>
            <p className="font-caption text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
              WhatsApp SLA surface — reply times, auto-replies, escalations and AI spend
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="text-sm px-2 py-1.5 rounded-lg border font-caption"
              style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-foreground)" }}
              aria-label="From date"
            />
            <span className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>to</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="text-sm px-2 py-1.5 rounded-lg border font-caption"
              style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-foreground)" }}
              aria-label="To date"
            />
            <Button variant="outline" size="sm" onClick={load} disabled={isLoading}>
              <Icon name="RefreshCw" size={13} style={{ marginRight: 5 }} />
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border px-4 py-3 flex items-center justify-between gap-4" style={{ borderColor: "var(--color-error)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-error)" }}>{error}</p>
            <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" iconSize={14} onClick={load}>
              Retry
            </Button>
          </div>
        )}

        {isLoading ? (
          <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>Loading…</p>
        ) : !data ? (
          <div className="rounded-xl border px-4 py-10 text-center" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <Icon name="AlertTriangle" size={20} color="var(--color-error)" className="mx-auto mb-2" />
            <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>
              We could not load the operations report. Check your connection and try again.
            </p>
            <Button variant="default" size="sm" className="mt-4" iconName="RefreshCw" iconPosition="left" iconSize={14} onClick={load}>
              Retry
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                icon="Clock"
                label="Avg first reply"
                value={avg === null ? "—" : `${avg} min`}
                sub={`${data.reply.repliedThreads} of ${data.reply.totalThreads} threads replied to`}
                tone={avg === null || avg <= 30 ? "green" : avg <= 120 ? "amber" : "red"}
              />
              <StatCard
                icon="Bot"
                label="Auto-replies"
                value={data.autoReplies}
                sub="off-hours messages handled automatically"
                tone="default"
              />
              <StatCard
                icon="Siren"
                label="Escalations"
                value={data.escalations}
                sub="unassigned threads past 2h → tasks"
                tone={data.escalations > 0 ? "amber" : "green"}
              />
              <StatCard
                icon="Zap"
                label="AI tokens"
                value={data.aiTokensInWindow.toLocaleString("en-IN")}
                sub="recorded draft/extraction usage in window"
                tone="default"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <StatCard
                icon="MessageCircle"
                label="Threads opened in window"
                value={data.threadsOpenedInWindow}
                sub="new customer conversations"
              />
              <StatCard
                icon="Inbox"
                label="Open threads now"
                value={data.openThreadsNow}
                sub="awaiting a human reply or assignment"
                tone={data.openThreadsNow > 0 ? "amber" : "green"}
              />
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}